import { getMyTrainingActiveCounts, type TrainingActiveCounts } from "@/api";
import { isHttpClientError } from "@/http";
import { PollingController, type PollingSnapshot } from "@/lifecycle";
import { createLogger } from "@/logging";

const logger = createLogger("components/activity");

/**
 * 마지막으로 기록한 실패의 형태다. 같은 실패가 이어지는 동안은 다시 기록하지 않는다.
 *
 * 사이드바는 모든 페이지에 상주하고 이 조회는 30초마다 실행된다. 실패를 주기마다 기록하면
 * 콘솔이 그 한 줄로 채워져 **실제 새 실패를 찾을 수 없다**. 조회가 성공하면 이 값을 비운다.
 * 그래서 나중에 같은 실패가 다시 발생하면 다시 기록한다.
 */
let lastLoggedFailure: string | null = null;

/** 테스트 전용이다. 모듈 싱글턴이므로 케이스 사이에 이 잠금 값이 남지 않게 비운다. */
export function resetActivityFailureLogForTest(): void {
  lastLoggedFailure = null;
}

/**
 * 실패를 기록한다. **4xx 는 경고, 나머지는 오류**다.
 *
 * BE 배포 전에는 이 경로가 없어 404 가 온다. 예상된 상태를 오류로 기록하면 경보 피로만 늘고,
 * 그 사이의 5xx·네트워크 끊김을 구분할 수 없다(`rules/error-handling.md` 의 로그 레벨).
 */
function reportLoadError(error: unknown): void {
  const http = isHttpClientError(error) ? error : null;
  const signature = http === null ? String(error) : `${http.kind}:${http.status ?? ""}`;
  if (signature === lastLoggedFailure) {
    return;
  }
  lastLoggedFailure = signature;
  const message = "Failed to count active training work.";
  const status = http?.status ?? null;
  if (status !== null && status >= 400 && status < 500) {
    logger.warn(message, error);
    return;
  }
  logger.error(message, error);
}

// 학습·eval 은 둘 다 분~시간 단위로 실행되는 작업이다. 그래서 GPU 가용량(15s)만큼 자주 조회할 필요가 없다.
export const trainingActivityPollIntervalMs = 30_000;

// data 는 내 스코프의 활성 학습·평가 건수다. 첫 로드 전에는 null 이므로 "0 개" 와 구분된다.
export type TrainingActivitySnapshot = PollingSnapshot<TrainingActiveCounts>;

/**
 * 내 스코프의 활성 학습·평가 건수를 센다.
 *
 * 두 축을 BE 집계 endpoint 하나로 받는다. 무엇이 "활성"인지는 상태 어휘를 소유한 쪽이 정해야
 * 한다. 그래야 어휘가 늘 때 개수가 경고 없이 어긋나지 않는다. 목록 API 의 `total` 을 다시 쓰면
 * 다음 비용이 생긴다:
 * - 항목 조립(실험 이름·선점자·노트·데이터셋) 비용.
 * - Eval 은 상태마다 한 번씩 조회해야 하므로 왕복이 늘어난다.
 *
 * 사이드바는 모든 페이지에 상주하고, 배지는 그중 두 항목에 있다. 그래서 폴링 주체를 모듈
 * 싱글턴 하나로 둔다. 실패는 **화면에는** 표시하지 않는다. 배지는 보조 정보라 실패를 알려도
 * 사용자가 할 일이 없다. BE 배포 전에는 이 경로가 없어 404 가 온다. 이때는 배지를 그리지 않는
 * 것이 맞는 동작이다. 로그에는 기록한다. 레벨을 구분하고, 같은 실패를 반복해서 기록하지 않는다
 * (`reportLoadError`).
 */
export const trainingActivityController = new PollingController<TrainingActiveCounts>({
  // 모듈 로드 시점의 참조를 고정하지 않는다. 호출할 때 찾아야 테스트가 조회 함수를 교체할 수 있다.
  load: async () => {
    const counts = await getMyTrainingActiveCounts();
    // 한 번 성공하면 다음 실패는 새 실패다. 그래서 다시 기록한다.
    lastLoggedFailure = null;
    return counts;
  },
  intervalMs: trainingActivityPollIntervalMs,
  onLoadError: reportLoadError,
});
